import type { ChartTypeCode } from '../api/chartConfig';
import {
    isAssetAllocationResponse,
    isCreditQualityResponse,
    isEconSectorResponse,
    isPerf10kResponse,
    isPricePerformanceResponse,
    isShoutTnaResponse,
    isSummaryCreditRatingResponse,
} from '../api/chartDataTypes';
import { AssetAllocationChartPanel } from './AssetAllocationChartPanel';
import { CreditQualityChartPanel } from './CreditQualityChartPanel';
import { EconSectorChartPanel } from './EconSectorChartPanel';
import { Perf10kChartPanel } from './Perf10kChartPanel';
import { PricePerformanceChartPanel } from './PricePerformanceChartPanel';
import { ShoutTnaChartPanel } from './ShoutTnaChartPanel';
import { SummaryCreditRatingChartPanel } from './SummaryCreditRatingChartPanel';

type ChartDataSectionProps = {
    /** Currently-selected chart type — drives which chart-specific panel runs.
     *  An empty string (no selection) renders nothing. */
    chartType: ChartTypeCode | '';
    /** Raw response payload from the generic chart-data query. Each branch
     *  below narrows it via its own type guard before forwarding. */
    chartData: unknown;
};

/**
 * Right-pane dispatcher. Picks the chart-type-specific orchestrator based on
 * the selected chart type and forwards the (narrowed) response data to it.
 * Every orchestrator renders ChartDataSectionShell internally for the shared
 * layout + announcements, so the visual chrome is identical across types.
 *
 * Keeps ChartReaderPage chart-type-agnostic — the page just fires a single
 * generic chart-data query and hands the result here without inspecting it.
 *
 * Adding a new chart type:
 *   1. Add a payload guard in `api/chartDataTypes.ts` (e.g. `isPriceResponse`).
 *   2. Build a `<FooChartPanel response={...}>` orchestrator mirroring
 *      `Perf10kChartPanel` — compute title/subtitle/summary from its response
 *      shape and render `ChartDataSectionShell` wrapping the chart-specific
 *      selector.
 *   3. Add a `case` below.
 *
 * Neither ChartReaderPage nor ChartDataSectionShell needs to change.
 */
export function ChartDataSection({ chartType, chartData }: ChartDataSectionProps) {
    switch (chartType) {
        case 'perf10k':
            return isPerf10kResponse(chartData) ? <Perf10kChartPanel response={chartData} /> : null;
        case 'shout_tna':
            return isShoutTnaResponse(chartData) ? <ShoutTnaChartPanel response={chartData} /> : null;
        case 'econ_sector':
            return isEconSectorResponse(chartData) ? <EconSectorChartPanel response={chartData} /> : null;
        case 'credit_quality':
            return isCreditQualityResponse(chartData) ? <CreditQualityChartPanel response={chartData} /> : null;
        case 'asset_allocation':
            return isAssetAllocationResponse(chartData) ? <AssetAllocationChartPanel response={chartData} /> : null;
        case 'summary_credit_rating':
            return isSummaryCreditRatingResponse(chartData) ? (
                <SummaryCreditRatingChartPanel response={chartData} />
            ) : null;
        case 'price_performance':
            return isPricePerformanceResponse(chartData) ? (
                <PricePerformanceChartPanel response={chartData} />
            ) : null;
        // case 'starsTrendComp':
        //     return isStarsTrendResponse(chartData) ? <StarsTrendPanel response={chartData} /> : null;
        case '':
            return null;
        default:
            return null;
    }
}
